<template>
  <div class="file-preview">
    <!-- 图片预览 -->
    <div v-if="isImage" class="image-preview">
      <el-image :src="value" :preview-src-list="[value]" fit="cover">
        <div slot="error" class="image-slot">
          <i class="el-icon-picture-outline"></i>
        </div>
      </el-image>
    </div>

    <!-- AMR 语音：浏览器不能直接播，走全局 AmrPlayer -->
    <div v-else-if="isAmr" class="amr-preview" :class="{ 'is-disabled': !amrReady }" @click="toggleAmr">
      <i :class="amrIcon"></i>
      <span>{{ amrText }}</span>
    </div>

    <!-- 音频预览。不要写死 audio/mpeg，通话录音是 wav，写死后浏览器会拒绝播放 -->
    <div v-else-if="isAudio" class="audio-preview">
      <audio controls :src="value">您的浏览器不支持音频播放</audio>
    </div>

    <!-- 视频预览 -->
    <div v-else-if="isVideo" class="video-preview">
      <video controls :src="value" class="video-player">您的浏览器不支持视频播放</video>
    </div>

    <!-- 其他文件 -->
    <div v-else class="other-preview">
      <el-link type="primary" :href="value" target="_blank" :underline="false">
        <i class="el-icon-document"></i>
        查看附件
      </el-link>
    </div>
  </div>
</template>

<script>
let currentAmrVm = null;

export default {
  name: "FilePreview",
  props: {
    value: {
      type: String,
      required: true
    },
    amr: {
      type: Boolean,
      default: false
    },
    /** 通话等已知是音频、但地址不一定以 .wav/.mp3 结尾时，直接按音频展示 */
    audio: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      amrReady: false,
      amrPlaying: false,
      amrError: false
    };
  },
  computed: {
    filePath() {
      return (this.value || "").split("?")[0].split("#")[0];
    },
    isImage() {
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(this.filePath);
    },
    isAmr() {
      return this.amr || /\.amr$/i.test(this.filePath);
    },
    isAudio() {
      if (this.audio) {
        return true;
      }
      // 后缀可能在查询参数里，例如 objectName=xxx.wav，不能只看路径结尾
      return /\.(mp3|wav|ogg|m4a)(?=$|[?#&])/i.test(this.value || "");
    },
    isVideo() {
      return /\.(mp4|webm|ogg)$/i.test(this.filePath);
    },
    amrIcon() {
      if (this.amrPlaying) {
        return "el-icon-video-pause";
      }
      if (this.amrReady) {
        return "el-icon-video-play";
      }
      if (this.amrError) {
        return "el-icon-warning-outline";
      }
      return "el-icon-loading";
    },
    amrText() {
      if (this.amrPlaying) {
        return "暂停语音";
      }
      if (this.amrReady) {
        return "播放语音";
      }
      if (this.amrError) {
        return "语音加载失败";
      }
      return "语音加载中";
    }
  },
  watch: {
    value() {
      this.initAmr();
    },
    amr() {
      this.initAmr();
    }
  },
  mounted() {
    this.initAmr();
  },
  beforeDestroy() {
    this.disposeAmr();
  },
  methods: {
    initAmr() {
      this.disposeAmr();
      if (!this.isAmr) {
        return;
      }
      this.amrReady = false;
      this.amrPlaying = false;
      this.amrError = false;
      if (typeof AmrPlayer === "undefined") {
        this.amrError = true;
        return;
      }
      const token = {};
      this._amrToken = token;
      const player = new AmrPlayer(this.value, () => {
        if (this._amrToken !== token) {
          return;
        }
        this.waitAmrSource(player, token, null);
      });
      player.endedWith(() => {
        if (this._amrToken !== token) {
          return;
        }
        this.amrPlaying = false;
        if (currentAmrVm === this) {
          currentAmrVm = null;
        }
        this.amrReady = false;
        this.waitAmrSource(player, token, player.bufferSource);
      });
      this._amrPlayer = player;
      this._amrTimer = setTimeout(() => {
        if (this._amrToken !== token || this.amrReady) {
          return;
        }
        this.amrError = true;
      }, 15000);
    },
    waitAmrSource(player, token, previousSource) {
      this.clearAmrTimer();
      const started = Date.now();
      const check = () => {
        if (this._amrToken !== token || this._amrPlayer !== player) {
          return;
        }
        const source = player.bufferSource;
        if (source && source !== previousSource) {
          this.amrError = false;
          this.amrReady = true;
          return;
        }
        if (Date.now() - started > 15000) {
          this.amrReady = false;
          this.amrError = true;
          return;
        }
        this._amrTimer = setTimeout(check, 50);
      };
      this._amrTimer = setTimeout(check, 50);
    },
    clearAmrTimer() {
      if (this._amrTimer) {
        clearTimeout(this._amrTimer);
        this._amrTimer = null;
      }
    },
    toggleAmr() {
      const player = this._amrPlayer;
      if (!player || !this.amrReady) {
        return;
      }
      if (this.amrPlaying) {
        this.stopAmr();
        return;
      }
      if (currentAmrVm && currentAmrVm !== this) {
        currentAmrVm.stopAmr();
      }
      const ctx = player.getAudioContext();
      if (ctx && ctx.state === "suspended" && ctx.resume) {
        ctx.resume();
      }
      player.play();
      this.amrPlaying = !!player.isPlaying;
      if (this.amrPlaying) {
        currentAmrVm = this;
      }
    },
    stopAmr() {
      const player = this._amrPlayer;
      if (player && player.isPlaying) {
        player.pause();
      }
      this.amrPlaying = false;
      if (currentAmrVm === this) {
        currentAmrVm = null;
      }
    },
    disposeAmr() {
      this.stopAmr();
      this.clearAmrTimer();
      this._amrToken = null;
      this._amrPlayer = null;
      this.amrReady = false;
      this.amrError = false;
    }
  }
};
</script>

<style scoped lang="less">
.file-preview {
  display: inline-block;

  .image-preview {
    max-width: 100px;
    max-height: 100px;
    cursor: pointer;

    .el-image {
      width: 100%;
      height: 100%;
      border-radius: 4px;
    }

    .image-slot {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background: #f5f7fa;
      color: #909399;
    }
  }

  .amr-preview {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 108px;
    padding: 6px 10px;
    border-radius: 4px;
    color: #409eff;
    cursor: pointer;
    user-select: none;

    &.is-disabled {
      color: #909399;
      cursor: default;
    }
  }

  .audio-preview {
    min-width: 300px;
    padding: 10px;
    background: #f5f7fa;
    border-radius: 4px;

    audio {
      width: 100%;
    }
  }

  .video-preview {
    width: 320px;
    background: #f5f7fa;
    border-radius: 4px;
    overflow: hidden;

    .video-player {
      width: 100%;
      max-height: 240px;
    }
  }

  .other-preview {
    padding: 10px;

    .el-link {
      display: flex;
      align-items: center;

      i {
        margin-right: 5px;
      }
    }
  }
}
</style>
